import { useState, useEffect, useCallback } from "react";
import EmployeeOnBoardService from "@/services/EmployeeOnBoard/EmployeeOnBoardService";
import { AttendanceService } from "@/services/Attendance/AttendanceService";
import { SalaryHeadServices } from "@/services/SalaryHead/SalaryHeadServices";
import type { IUser } from "@/interface/employeeOnBoard";
import type { IAttendanceSummaryUser } from "@/interface/attendance.interface";
import { ThrowError } from "@/api/callApi";
import {
  buildEmployeeQuery,
  buildAttendanceQuery,
} from "./createSalary.utils";
import type { UseMasterDataReturn } from "./useMasterData";

export type EmployeeSelectionMode = "all" | "configured" | "not_configured" | "clear";
export type AttendanceSelectionMode = "all" | "not_locked" | "locked_pending_salary" | "clear";
export type SelectionMode = EmployeeSelectionMode | AttendanceSelectionMode;
export type SelectionType =
  | "all"
  | "configured"
  | "not_configured"
  | "not_locked"
  | "locked_pending_salary"
  | "custom"
  | "none";

export const useEmployeeSelection = (
  masterData: UseMasterDataReturn,
  employees: IUser[],
  attendanceList: IAttendanceSummaryUser[],
) => {
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<
    Set<string | number>
  >(new Set());
  const [isAllBranchUsersSelected, setIsAllBranchUsersSelected] =
    useState<boolean>(false);
  const [selectionType, setSelectionType] = useState<SelectionType>("none");
  const [isSelectingUsers, setIsSelectingUsers] = useState<boolean>(false);

  // Auto-check newly fetched page users when select-all is active without redundant re-renders
  useEffect(() => {
    if (isAllBranchUsersSelected) {
      const list =
        masterData.activeTab === "attendance"
          ? attendanceList
              .filter((item) => !item.salaryCreated?.isCreated)
              .map((item) => item.user?.id)
              .filter(Boolean)
          : employees.map((emp) => emp.id).filter(Boolean);

      if (list.length > 0) {
        setSelectedEmployeeIds((prev) => {
          let hasNew = false;
          for (const id of list) {
            if (!prev.has(String(id))) {
              hasNew = true;
              break;
            }
          }
          if (!hasNew) return prev;
          const next = new Set(prev);
          list.forEach((id) => next.add(String(id)));
          return next;
        });
      }
    }
  }, [
    employees,
    attendanceList,
    isAllBranchUsersSelected,
    masterData.activeTab,
  ]);

  // Reset selection when attendance date range changes to prevent stale selections from previous month
  useEffect(() => {
    setSelectedEmployeeIds(new Set());
    setIsAllBranchUsersSelected(false);
    setSelectionType("none");
  }, [masterData.attendanceStartDate, masterData.attendanceEndDate]);

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      const newSelected = new Set(selectedEmployeeIds);
      if (checked) {
        employees.forEach((emp) => {
          if (emp.id) newSelected.add(String(emp.id));
        });
      } else {
        employees.forEach((emp) => {
          if (emp.id) newSelected.delete(String(emp.id));
        });
        setIsAllBranchUsersSelected(false);
      }
      setSelectionType("custom");
      setSelectedEmployeeIds(newSelected);
    },
    [selectedEmployeeIds, employees],
  );

  const handleAttendanceSelectAll = useCallback(
    (checked: boolean) => {
      const newSelected = new Set(selectedEmployeeIds);
      if (checked) {
        attendanceList.forEach((item) => {
          if (item.user?.id && !item.salaryCreated?.isCreated) {
            newSelected.add(String(item.user.id));
          }
        });
      } else {
        attendanceList.forEach((item) => {
          if (item.user?.id) newSelected.delete(String(item.user.id));
        });
        setIsAllBranchUsersSelected(false);
      }
      setSelectionType("custom");
      setSelectedEmployeeIds(newSelected);
    },
    [selectedEmployeeIds, attendanceList],
  );

  const handleSelectItem = useCallback(
    (itemOrId: IUser | IAttendanceSummaryUser | string, checked: boolean) => {
      if (
        typeof itemOrId === "object" &&
        "salaryCreated" in itemOrId &&
        (itemOrId as IAttendanceSummaryUser).salaryCreated?.isCreated
      ) {
        return;
      }

      const itemId =
        typeof itemOrId === "object"
          ? "user" in itemOrId && (itemOrId as IAttendanceSummaryUser).user?.id
            ? String((itemOrId as IAttendanceSummaryUser).user.id)
            : String((itemOrId as IUser).id)
          : String(itemOrId);

      if (!itemId) return;
      setSelectedEmployeeIds((prev) => {
        const next = new Set(prev);
        if (checked) {
          next.add(itemId);
        } else {
          next.delete(itemId);
          setIsAllBranchUsersSelected(false);
        }
        return next;
      });
      setSelectionType("custom");
    },
    [],
  );

  const handleSelectUsersByMode = useCallback(
    async (mode: SelectionMode) => {
      if (mode === "clear") {
        setIsAllBranchUsersSelected(false);
        setSelectionType("none");
        setSelectedEmployeeIds(new Set());
        return;
      }

      setIsSelectingUsers(true);
      try {
        if (mode === "all") {
          setIsAllBranchUsersSelected(true);
          setSelectionType("all");

          // Instant local check for visible items
          const instantIds = new Set<string | number>();
          if (masterData.activeTab === "attendance") {
            attendanceList.forEach((item) => {
              if (item.user?.id && !item.salaryCreated?.isCreated) {
                instantIds.add(String(item.user.id));
              }
            });
          } else {
            employees.forEach((emp) => {
              if (emp.id) instantIds.add(String(emp.id));
            });
          }
          if (instantIds.size > 0) {
            setSelectedEmployeeIds(instantIds);
          }

          if (masterData.activeTab === "attendance") {
            const response = await AttendanceService.getDailyTeamAttendance(
              buildAttendanceQuery(
                {
                  selectedBranchId: masterData.selectedBranchId,
                  selectedSiteId: masterData.selectedSiteId,
                  selectedCategory: masterData.selectedCategory,
                  searchValue: masterData.searchValue,
                  attendanceStartDate: masterData.attendanceStartDate,
                  attendanceEndDate: masterData.attendanceEndDate,
                },
                { currentPage: 1, currentLimit: 10000 },
              ),
            );
            if (response?.status && response?.data?.users) {
              const allIds = new Set<string | number>();
              response.data.users.forEach((item: IAttendanceSummaryUser) => {
                if (item.user?.id && !item.salaryCreated?.isCreated) {
                  allIds.add(String(item.user.id));
                }
              });
              setSelectedEmployeeIds(allIds);
            }
          } else {
            const response = await EmployeeOnBoardService.getAllEmployees(
              buildEmployeeQuery(
                {
                  selectedBranchId: masterData.selectedBranchId,
                  selectedSiteId: masterData.selectedSiteId,
                  selectedCategory: masterData.selectedCategory,
                  searchValue: masterData.searchValue,
                },
                { currentPage: 1, currentLimit: 10000 },
              ),
            );
            if (response?.status && response?.data?.user) {
              const allIds = new Set<string | number>();
              response.data.user.forEach((emp: IUser) => {
                if (emp.id) allIds.add(String(emp.id));
              });
              setSelectedEmployeeIds(allIds);
            }
          }
        } else if (mode === "not_locked" || mode === "locked_pending_salary") {
          setIsAllBranchUsersSelected(false);
          setSelectionType(mode);

          // Instant local check for visible attendance items
          const instantIds = new Set<string | number>();
          attendanceList.forEach((item) => {
            if (!item.user?.id || item.salaryCreated?.isCreated) return;
            const isLocked = Boolean(item.attendanceLock?.isLocked);
            if (mode === "not_locked" && !isLocked) {
              instantIds.add(String(item.user.id));
            } else if (mode === "locked_pending_salary" && isLocked) {
              instantIds.add(String(item.user.id));
            }
          });
          if (instantIds.size > 0) {
            setSelectedEmployeeIds(instantIds);
          }

          // Complete branch/site fetch
          const response = await AttendanceService.getDailyTeamAttendance(
            buildAttendanceQuery(
              {
                selectedBranchId: masterData.selectedBranchId,
                selectedSiteId: masterData.selectedSiteId,
                selectedCategory: masterData.selectedCategory,
                searchValue: masterData.searchValue,
                attendanceStartDate: masterData.attendanceStartDate,
                attendanceEndDate: masterData.attendanceEndDate,
              },
              { currentPage: 1, currentLimit: 10000 },
            ),
          );

          if (response?.status && response?.data?.users) {
            const targetIds = new Set<string | number>();
            response.data.users.forEach((item: IAttendanceSummaryUser) => {
              if (!item.user?.id || item.salaryCreated?.isCreated) return;
              const isLocked = Boolean(item.attendanceLock?.isLocked);
              if (mode === "not_locked" && !isLocked) {
                targetIds.add(String(item.user.id));
              } else if (mode === "locked_pending_salary" && isLocked) {
                targetIds.add(String(item.user.id));
              }
            });
            setSelectedEmployeeIds(targetIds);
          }
        } else if (mode === "configured" || mode === "not_configured") {
          setIsAllBranchUsersSelected(false);
          setSelectionType(mode);

          const queryParams = new URLSearchParams();
          if (masterData.selectedBranchId)
            queryParams.append("branchId", masterData.selectedBranchId);
          if (masterData.selectedSiteId)
            queryParams.append("siteId", masterData.selectedSiteId);
          if (masterData.selectedCategory)
            queryParams.append("category", masterData.selectedCategory);
          if (masterData.searchValue)
            queryParams.append("search", masterData.searchValue);
          queryParams.append("pageNumber", "1");
          queryParams.append("pageSize", "10000");

          const response = await SalaryHeadServices.getBranchWiseUserSalary(
            queryParams.toString(),
          );

          if (response?.status || response?.statuscode === 200) {
            const usersList = response?.data?.users || response?.data?.user || [];
            const targetIds = new Set<string | number>();

            usersList.forEach((u: any) => {
              const hasSalaryConfig =
                Array.isArray(u.salaries) && u.salaries.length > 0;
              if (mode === "configured" && hasSalaryConfig && u.id) {
                targetIds.add(String(u.id));
              } else if (mode === "not_configured" && !hasSalaryConfig && u.id) {
                targetIds.add(String(u.id));
              }
            });

            setSelectedEmployeeIds(targetIds);
          }
        }
      } catch (error) {
        ThrowError(error);
      } finally {
        setIsSelectingUsers(false);
      }
    },
    [masterData, employees, attendanceList],
  );

  const handleToggleSelectAllBranchUsers = useCallback(async () => {
    if (isAllBranchUsersSelected || selectionType !== "none" || selectedEmployeeIds.size > 0) {
      await handleSelectUsersByMode("clear");
    } else {
      await handleSelectUsersByMode("all");
    }
  }, [isAllBranchUsersSelected, selectionType, selectedEmployeeIds.size, handleSelectUsersByMode]);

  // Clear selection when filter changes
  const clearSelection = useCallback(() => {
    setSelectedEmployeeIds(new Set());
    setIsAllBranchUsersSelected(false);
    setSelectionType("none");
  }, []);

  return {
    selectedEmployeeIds,
    isAllBranchUsersSelected,
    selectionType,
    isSelectingUsers,
    handleSelectAll,
    handleAttendanceSelectAll,
    handleSelectItem,
    handleSelectUsersByMode,
    handleToggleSelectAllBranchUsers,
    clearSelection,
  };
};

export type UseEmployeeSelectionReturn = ReturnType<
  typeof useEmployeeSelection
>;
